/**
 * YouTube facade: no third-party request until the visitor asks for the film, and none at all
 * until embedded content is consented to.
 *
 * <p>The video id is read from `data-video` on the button, so one function serves every film
 * surface without an id hardcoded in the script.</p>
 *
 * <p>A visitor who has already said yes plays on one click, exactly as before. One who has not
 * gets the reason in the poster's place and an accept button; granting from anywhere on the page
 * then starts the film that was actually asked for, and only that one.</p>
 */
import { findAll } from "./dom";
import { consentPrompt, hasEmbedConsent, onConsentChange } from "./consent";

export function initVideoFacades(): void {
  for (const button of findAll<HTMLButtonElement>("[data-film]")) {
    const wrap = button.parentElement;
    if (wrap === null) {
      continue;
    }

    const videoId = button.dataset["video"] ?? "";
    if (videoId === "") {
      continue;
    }

    const title = button.dataset["videoTitle"] ?? "Film om Åmund Vin och Fruktgård";

    // Set once this facade is the one waiting for an answer, so granting consent starts this
    // film rather than every film on the page.
    let asked = false;

    const play = (): void => {
      const frame = document.createElement("iframe");
      frame.src =
        `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}` +
        "?autoplay=1&rel=0&modestbranding=1";
      frame.title = title;
      frame.allow = "accelerometer; autoplay; encrypted-media; picture-in-picture";
      frame.allowFullscreen = true;
      frame.style.cssText = "width:100%;aspect-ratio:16/9;border:0;display:block";

      wrap.replaceChildren(frame);
      frame.focus();
    };

    button.addEventListener("click", () => {
      if (hasEmbedConsent()) {
        play();
        return;
      }

      asked = true;
      // Replacing the button rather than the whole box keeps the poster and the box's height.
      button.replaceWith(consentPrompt(
        "Filmen spelas via YouTube, som sätter egna cookies.",
        "Godkänn och spela filmen",
      ));
    });

    onConsentChange((granted) => {
      if (granted && asked) {
        play();
      }
    });
  }
}
